import { extend } from 'flarum/common/extend';
import TagRulesNotice from './components/TagRulesNotice';
// rulesDiscussionId() itself comes from this module's own Tag augmentation
// (registerTagAttributes() below) - imported for its side effect and for
// the type it declares.
import registerTagAttributes from './utils/tagAttributes';

/**
 * Shows the selected tag's own configured rules (Tag.rulesDiscussionId,
 * see champis-forum's TagResource field + migration) above the "Nouvelle
 * discussion" composer, once a tag with one set is picked - the Flarum
 * equivalent of the old phpBB site's own posting_rules_listener.php (see
 * that field's own migration docblock for the exact precedent this
 * replaces).
 *
 * Renders TagRulesNotice (the rules content itself, dismissible) - see
 * that component's own docblock for why this isn't a link opening a modal
 * the way it used to be.
 *
 * Injected as an extra child of Composer's own root .Composer div (via
 * extend() on its view(), pushing onto the returned vnode's children -
 * same "mutate the returned value in place" convention core's own
 * SettingsPage.settingsItems() uses), positioned absolutely
 * (`bottom: 100%` - see champis-theme's own forum.less) rather than
 * flowing normally inside it. That's deliberate, not incidental: Composer
 * itself sets its root element's height via jQuery every redraw
 * (`this.$().height(this.state.computedHeight())` in updateHeight()) to a
 * fixed pixel value - growing *flow* content inside it (an earlier
 * version tried rendering this in DiscussionComposer's own headerItems(),
 * a normal list item above the editor) doesn't grow that computed height
 * at all, it just leaves less of that same fixed budget for
 * `.Composer-flexible` (the editor), which is the one thing updateHeight()
 * explicitly resizes to fill whatever's left - confirmed directly
 * ("il prend toute la place du composer"). An absolutely positioned
 * child, by contrast, is invisible to that whole calculation (out of
 * flow), so it can be as tall as it needs to be without shrinking the
 * editor by a single pixel - "le composer doit garder sa taille". Being a
 * child of .Composer itself (not a sibling mounted elsewhere in the page,
 * which an earlier version of this also tried) is what guarantees
 * identical width/alignment for free, `left: 0; right: 0` against the
 * same .position: relative box - "lié au composer et avec la même
 * largeur", both requested directly after seeing the sibling-mount
 * version render narrower and visually disconnected.
 *
 * Only shown in the composer's *normal* (bottom-docked) position -
 * fullScreen already has all the room it needs without this (and already
 * covers the full viewport, so "above" it wouldn't be visible anyway),
 * and minimized is just a thin title bar with nothing to show rules
 * above.
 *
 * Same extension point family (reading composer.fields.tags reactively)
 * champis-species' own addSpeciesComposer.js and this file's own earlier
 * version already used for the same "react to the tag picked in this
 * composer" need.
 *
 * Only the *first* selected tag with a rulesDiscussionId shows - matches
 * the phpBB precedent's own "one forum, one set of rules" simplicity; a
 * discussion picking multiple tags that each have their own rules
 * configured is not expected to happen in practice (rulesDiscussionId is
 * really only meaningful on the handful of tags that ever had a phpBB-side
 * forum_rules equivalent).
 */
export default function showTagRulesInComposer() {
  registerTagAttributes();

  // String target, not a static `import Composer from '...'` - Composer
  // is one of core's own code-split chunks (confirmed via
  // flarum.reg.chunkModules; ComposerState.show() itself only ever gets
  // it via `await import(/* webpackChunkName: 'components/Composer', webpackMode: 'lazy-once' */ '../components/Composer')`, never a static
  // import), so it isn't guaranteed to already be loaded when this
  // initializer runs. extend()'s string form defers resolving/patching
  // the target until it's actually required instead of needing it
  // present right now, same reason the original version of this (see git
  // history) targeted DiscussionComposer this way rather than importing
  // it directly.
  extend('flarum/forum/components/Composer', 'view', function (vnode) {
    const composer = this.state;

    // 'normal' literally, not ComposerState.Position.NORMAL - avoids a
    // static `import ComposerState from 'flarum/forum/states/ComposerState'`
    // for the same reason as Composer above (also lazily chunked per
    // flarum.reg.chunkModules). Its Position enum is a plain, stable
    // `{ HIDDEN: 'hidden', NORMAL: 'normal', MINIMIZED: 'minimized',
    // FULLSCREEN: 'fullScreen' }` (see core's ComposerState.tsx) - not
    // worth an import just for one constant.
    if (composer.position !== 'normal') return;
    const tags = composer.fields?.tags || [];
    const tagWithRules = tags.find(tag => tag.rulesDiscussionId());
    if (!tagWithRules) return;
    const discussionId = tagWithRules.rulesDiscussionId();

    // No `key` here - see TagRulesNotice.js's own docblock for why: mixed
    // with the other, unkeyed children Composer.view() already builds
    // (Composer-handle, Composer-controls, Composer-content), a key on
    // just this one doesn't reliably force a remount when discussionId
    // changes. TagRulesNotice's own onbeforeupdate() handles that instead.
    vnode.children.push(m(TagRulesNotice, {
      discussionId: discussionId
    }));
  });
}